/**
 * Webtech HR Engine™ - Page de Gestion des Objectifs
 * Interface complète pour créer, visualiser et gérer les objectifs
 * selon les 5 types : Entreprise, Département, Équipe, Individuel, IA
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Target,
  Building2,
  Users,
  UserCircle,
  Sparkles,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Loader2,
  Search,
  Filter,
  ChevronRight,
  Award,
  Lightbulb,
  RefreshCw,
  Send,
  Eye,
  Scale
} from 'lucide-react';

// ==================== CONSTANTS ====================

const OBJECTIVE_TYPES = {
  enterprise: { label: 'Entreprise', icon: Building2, color: 'bg-blue-100 text-blue-700', description: 'Objectif global de l\'entreprise' },
  department: { label: 'Département', icon: Users, color: 'bg-green-100 text-green-700', description: 'Objectif d\'un département' },
  team: { label: 'Équipe', icon: Users, color: 'bg-purple-100 text-purple-700', description: 'Objectif d\'une équipe' },
  individual: { label: 'Individuel', icon: UserCircle, color: 'bg-amber-100 text-amber-700', description: 'Objectif personnel' },
  ai_suggestion: { label: 'Suggestion IA', icon: Sparkles, color: 'bg-pink-100 text-pink-700', description: 'Suggéré par l\'IA' }
};

const STATUS_CONFIG = {
  proposed: { label: 'Proposé', icon: Clock, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  validated: { label: 'Validé', icon: CheckCircle2, color: 'bg-green-100 text-green-700 border-green-200' },
  in_progress: { label: 'En cours', icon: TrendingUp, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  completed: { label: 'Terminé', icon: Award, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejeté', icon: XCircle, color: 'bg-red-100 text-red-700 border-red-200' }
};

// ==================== COMPONENTS ====================

// Type badge
const TypeBadge = ({ type }) => {
  const config = OBJECTIVE_TYPES[type] || OBJECTIVE_TYPES.individual;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} border-0 flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

// Status badge
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.proposed;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} border flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

// Objective card
const ObjectiveCard = ({ objective, onEdit, onDelete, onValidate, canValidate, canEdit }) => {
  const typeConfig = OBJECTIVE_TYPES[objective.type] || OBJECTIVE_TYPES.individual;
  const TypeIcon = typeConfig.icon;
  
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg ${typeConfig.color} flex items-center justify-center flex-shrink-0`}>
              <TypeIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-slate-900 truncate">{objective.title}</h3>
                {objective.type === 'ai_suggestion' && (
                  <Badge className="bg-pink-100 text-pink-700 text-xs">IA</Badge>
                )}
              </div>
              <p className="text-sm text-slate-600 line-clamp-2">{objective.description}</p>
              
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <StatusBadge status={objective.status} />
                <Badge variant="outline" className="flex items-center gap-1">
                  <Scale className="w-3 h-3" />
                  {objective.weight}%
                </Badge>
                {objective.endDate && (
                  <span className="text-xs text-slate-500">
                    Échéance: {new Date(objective.endDate).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>
              
              {/* Progress bar if has evaluations */}
              {(objective.managerEvaluation?.score || objective.evaluations?.managerScore) && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Score: {objective.managerEvaluation?.score || objective.evaluations?.managerScore}%</span>
                    <span>Pondéré: {((objective.managerEvaluation?.score || objective.evaluations?.managerScore || 0) * (objective.weight || 0) / 100).toFixed(1)} pts</span>
                  </div>
                  <Progress value={objective.managerEvaluation?.score || objective.evaluations?.managerScore || 0} className="h-1.5" />
                </div>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-50">
              <DropdownMenuItem onSelect={() => onEdit(objective, false)}>
                <Eye className="w-4 h-4 mr-2" />
                Voir détails
              </DropdownMenuItem>
              {canEdit && objective.status !== 'completed' && (
                <DropdownMenuItem onSelect={() => onEdit(objective, true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </DropdownMenuItem>
              )}
              {canValidate && objective.status === 'proposed' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => onValidate(objective, true)} className="text-green-600">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Valider
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onValidate(objective, false)} className="text-red-600">
                    <XCircle className="w-4 h-4 mr-2" />
                    Rejeter
                  </DropdownMenuItem>
                </>
              )}
              {canEdit && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => onDelete(objective)} className="text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

// Create objective dialog
const CreateObjectiveDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  departments = [], 
  employees = [], 
  campaigns = [],
  userRole,
  isPro
}) => {
  const [formData, setFormData] = useState({
    type: 'individual',
    title: '',
    description: '',
    weight: 15,
    targetDepartmentId: '',
    targetEmployeeId: '',
    campaignId: '',
    startDate: '',
    endDate: '',
    kpis: []
  });
  const [kpiInput, setKpiInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Determine available types based on role
  const availableTypes = [];
  if (userRole === 'Admin' || userRole === 'SuperAdmin') {
    availableTypes.push('enterprise', 'department');
  }
  if (userRole === 'Manager' || userRole === 'Admin' || userRole === 'SuperAdmin') {
    availableTypes.push('team');
  }
  availableTypes.push('individual');
  if (isPro) {
    availableTypes.push('ai_suggestion');
  }
  
  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
      setFormData({
        type: 'individual',
        title: '',
        description: '',
        weight: 15,
        targetDepartmentId: '',
        targetEmployeeId: '',
        campaignId: '',
        startDate: '',
        endDate: '',
        kpis: []
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  
  const addKpi = () => {
    if (kpiInput.trim()) {
      setFormData(prev => ({
        ...prev,
        kpis: [...prev.kpis, kpiInput.trim()]
      }));
      setKpiInput('');
    }
  };
  
  const removeKpi = (index) => {
    setFormData(prev => ({
      ...prev,
      kpis: prev.kpis.filter((_, i) => i !== index)
    }));
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Créer un nouvel objectif
          </DialogTitle>
          <DialogDescription>
            Définissez un objectif SMART avec des critères mesurables
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Type */}
          <div className="space-y-2">
            <Label>Type d'objectif *</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {availableTypes.map(type => {
                const config = OBJECTIVE_TYPES[type];
                const Icon = config.icon;
                return (
                  <Button
                    key={type}
                    type="button"
                    variant={formData.type === type ? 'default' : 'outline'}
                    className="justify-start"
                    onClick={() => setFormData(prev => ({ ...prev, type }))}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {config.label}
                  </Button>
                );
              })}
            </div>
          </div>
          
          {/* Target selection based on type */}
          {formData.type === 'department' && (
            <div className="space-y-2">
              <Label>Département cible *</Label>
              <Select
                value={formData.targetDepartmentId}
                onValueChange={(value) => setFormData(prev => ({ ...prev, targetDepartmentId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un département" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {formData.type === 'individual' && (
            <div className="space-y-2">
              <Label>Employé cible</Label>
              <Select
                value={formData.targetEmployeeId}
                onValueChange={(value) => setFormData(prev => ({ ...prev, targetEmployeeId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un employé (ou vous-même)" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Campaign */}
          {campaigns.length > 0 && (
            <div className="space-y-2">
              <Label>Campagne associée (optionnel)</Label>
              <Select
                value={formData.campaignId}
                onValueChange={(value) => setFormData(prev => ({ ...prev, campaignId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une campagne" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  {campaigns.map(camp => (
                    <SelectItem key={camp.id} value={camp.id}>{camp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Title */}
          <div className="space-y-2">
            <Label>Titre de l'objectif *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ex: Augmenter le chiffre d'affaires de 15%"
            />
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label>Description détaillée *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Décrivez l'objectif, les critères de succès et les résultats attendus..."
              rows={3}
            />
          </div>
          
          {/* Weight */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Pondération</Label>
              <span className="text-sm font-medium text-blue-600">{formData.weight}%</span>
            </div>
            <Slider
              value={[formData.weight]}
              onValueChange={([value]) => setFormData(prev => ({ ...prev, weight: value }))}
              min={5}
              max={50}
              step={5}
              className="py-2"
            />
            <p className="text-xs text-slate-500">
              La somme des pondérations devrait atteindre 100%
            </p>
          </div>
          
          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date de début</Label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Date de fin</Label>
              <Input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>
          
          {/* KPIs */}
          <div className="space-y-2">
            <Label>Indicateurs clés (KPIs)</Label>
            <div className="flex gap-2">
              <Input
                value={kpiInput}
                onChange={(e) => setKpiInput(e.target.value)}
                placeholder="Ex: Taux de satisfaction client"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKpi())}
              />
              <Button type="button" variant="outline" onClick={addKpi}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.kpis.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.kpis.map((kpi, i) => (
                  <Badge key={i} variant="secondary" className="flex items-center gap-1">
                    {kpi}
                    <button onClick={() => removeKpi(i)} className="ml-1 hover:text-red-500">
                      <XCircle className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Création...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Créer l'objectif
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== MAIN PAGE ====================

export default function ObjectivesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPro, isFree, loading: planLoading } = usePlan();
  
  const [loading, setLoading] = useState(true);
  const [objectives, setObjectives] = useState([]);
  const [stats, setStats] = useState({});
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // View/Edit dialog state
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [objectiveToDelete, setObjectiveToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // AI Suggestions state
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingAiSuggestions, setLoadingAiSuggestions] = useState(false);
  const [selectedEmployeeForAI, setSelectedEmployeeForAI] = useState('');
  
  // Load data
  const fetchData = useCallback(async () => {
    if (!user?.id || !user?.companyId) return;
    
    try {
      const [objRes, deptRes, empRes, campRes] = await Promise.all([
        api.get('/objectives').catch(() => ({ data: { objectives: [], stats: {} } })),
        api.get('/departments/all').catch(() => ({ data: [] })),
        api.get('/employees/all').catch(() => ({ data: [] })),
        api.get(`/campaigns?company_id=${user.companyId}`).catch(() => ({ data: { campaigns: [] } }))
      ]);
      
      setObjectives(objRes.data?.objectives || []);
      setStats(objRes.data?.stats || {});
      setDepartments(deptRes.data || []);
      setEmployees(empRes.data || []);
      setCampaigns(campRes.data?.campaigns || []);
      
    } catch (error) {
      console.error('Error fetching objectives:', error);
      toast.error('Erreur lors du chargement des objectifs');
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.companyId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Create objective
  const handleCreateObjective = async (formData) => {
    try {
      // Clean up "none" values before sending
      const cleanData = {
        ...formData,
        campaignId: formData.campaignId === 'none' ? null : formData.campaignId
      };
      const response = await api.post('/objectives', cleanData);
      
      if (response.data?.needsValidation) {
        toast.success('📝 Objectif proposé. En attente de validation par votre manager.');
      } else {
        toast.success('✅ Objectif créé avec succès');
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
      throw error;
    }
  };
  
  // Validate objective
  const handleValidate = async (objective, approved) => {
    try {
      await api.post(`/objectives/${objective.id}/validate?approved=${approved}`);
      toast.success(approved ? '⭐ Objectif validé' : '❌ Objectif rejeté');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };
  
  // Delete objective
  const handleDelete = (objective) => {
    setObjectiveToDelete(objective);
    setShowDeleteDialog(true);
  };
  
  const confirmDelete = async () => {
    if (!objectiveToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/objectives/${objectiveToDelete.id}`);
      toast.success('🗑️ Objectif supprimé');
      setShowDeleteDialog(false);
      setObjectiveToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };
  
  // View/Edit objective
  const handleEdit = (objective, isEditMode = false) => {
    setSelectedObjective(objective);
    setEditFormData({
      title: objective.title || '',
      description: objective.description || '',
      weight: objective.weight || 20,
      status: objective.status || 'validated',
      endDate: objective.endDate || ''
    });
    setEditMode(isEditMode);
    setShowViewDialog(true);
  };
  
  // Save edited objective
  const handleSaveEdit = async () => {
    if (!selectedObjective) return;
    
    setSaving(true);
    try {
      await api.put(`/objectives/${selectedObjective.id}`, editFormData);
      toast.success('✅ Objectif mis à jour');
      setShowViewDialog(false);
      setSelectedObjective(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };
  
  // Generate AI suggestions for an employee
  const generateAiSuggestions = async (employeeId) => {
    if (!employeeId) {
      toast.error('Veuillez sélectionner un employé');
      return;
    }
    
    setLoadingAiSuggestions(true);
    setAiSuggestions([]);
    
    try {
      const response = await api.post(`/evaluation-workflow/suggest-objectives?employee_id=${employeeId}`);
      const suggestions = response.data?.suggestions || [];
      
      if (suggestions.length > 0) {
        setAiSuggestions(suggestions);
        toast.success(`✨ ${suggestions.length} suggestions générées par l'IA`);
      } else {
        toast.info('Aucune suggestion générée');
      }
    } catch (error) {
      console.error('AI suggestion error:', error);
      toast.error('Erreur lors de la génération des suggestions');
    } finally {
      setLoadingAiSuggestions(false);
    }
  };
  
  // Adopt an AI suggestion (create objective from it)
  const adoptAiSuggestion = async (suggestion) => {
    try {
      const objectiveData = {
        title: suggestion.title,
        description: suggestion.description,
        type: 'ai_suggestion',
        weight: suggestion.suggested_weight || 15,
        targetEmployeeId: selectedEmployeeForAI,
        kpis: suggestion.kpis || [],
        startDate: new Date().toISOString().split('T')[0],
        endDate: suggestion.timeframe?.includes('Q1') 
          ? '2025-03-31' 
          : suggestion.timeframe?.includes('Q2')
            ? '2025-06-30'
            : '2025-12-31'
      };
      
      const response = await api.post('/objectives', objectiveData);
      toast.success('🎯 Objectif créé depuis la suggestion IA');
      
      // Remove from suggestions list and refresh
      setAiSuggestions(prev => prev.filter(s => s.title !== suggestion.title));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };
  
  // Filter objectives
  const filteredObjectives = objectives.filter(obj => {
    // Tab filter
    if (activeTab !== 'all' && obj.type !== activeTab) return false;
    
    // Status filter
    if (statusFilter !== 'all' && obj.status !== statusFilter) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        obj.title?.toLowerCase().includes(query) ||
        obj.description?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });
  
  // Permissions
  const canValidate = user?.role === 'Manager' || user?.role === 'Admin' || user?.role === 'SuperAdmin';
  const canEdit = user?.role === 'Admin' || user?.role === 'SuperAdmin';
  
  if (loading || planLoading) {
    return (
      <DashboardLayout title="Objectifs">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="Objectifs">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Gestion des Objectifs</h1>
            <p className="text-slate-600 mt-1">
              Définissez et suivez les objectifs de performance
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvel objectif
          </Button>
        </div>
        
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(OBJECTIVE_TYPES).map(([type, config]) => {
            const Icon = config.icon;
            const count = stats[type] || 0;
            return (
              <Card 
                key={type} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${activeTab === type ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setActiveTab(type)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{count}</p>
                      <p className="text-xs text-slate-500">{config.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Pending validation alert */}
        {stats.pending_validation > 0 && canValidate && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="text-amber-800 font-medium">
                  {stats.pending_validation} objectif(s) en attente de validation
                </span>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setStatusFilter('proposed')}
              >
                Voir
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Weight warning */}
        {stats.total_weight !== undefined && (stats.total_weight < 95 || stats.total_weight > 105) && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Scale className="w-5 h-5 text-blue-600" />
              <span className="text-blue-800">
                Pondération totale: <strong>{stats.total_weight}%</strong>
                {stats.total_weight < 95 && ' — Ajoutez des objectifs pour atteindre 100%'}
                {stats.total_weight > 105 && ' — Réduisez les pondérations pour rester proche de 100%'}
              </span>
            </CardContent>
          </Card>
        )}
        
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher un objectif..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={() => { setActiveTab('all'); setStatusFilter('all'); setSearchQuery(''); }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">
              Tous ({objectives.length})
            </TabsTrigger>
            {Object.entries(OBJECTIVE_TYPES).map(([type, config]) => (
              <TabsTrigger key={type} value={type} className="hidden md:flex">
                {config.label} ({stats[type] || 0})
              </TabsTrigger>
            ))}
          </TabsList>
          
          <TabsContent value={activeTab} className="mt-6">
            {filteredObjectives.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {filteredObjectives.map(objective => (
                  <ObjectiveCard
                    key={objective.id}
                    objective={objective}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onValidate={handleValidate}
                    canValidate={canValidate}
                    canEdit={canEdit || objective.createdBy === user?.id}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Target className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Aucun objectif trouvé
                  </h3>
                  <p className="text-slate-600 max-w-md mx-auto mb-4">
                    {activeTab === 'all' 
                      ? "Commencez par créer vos premiers objectifs pour structurer l'évaluation de performance."
                      : `Aucun objectif de type "${OBJECTIVE_TYPES[activeTab]?.label}" n'a été créé.`}
                  </p>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un objectif
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* AI Suggestions (PRO) */}
        {isPro && (
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Suggestions IA
                <Badge className="bg-purple-100 text-purple-700 ml-2">BUSINESS</Badge>
                <Badge className="bg-pink-100 text-pink-700 ml-1">GPT-5.1</Badge>
              </CardTitle>
              <CardDescription>
                L'IA génère des objectifs personnalisés basés sur le profil de l'employé. Cliquez sur "Adopter" pour créer l'objectif automatiquement.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Employee selector and generate button */}
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label className="text-sm font-medium mb-1.5 block">Employé cible</Label>
                  <Select value={selectedEmployeeForAI} onValueChange={setSelectedEmployeeForAI}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un employé..." />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.firstName} {emp.lastName} - {emp.jobTitle || 'Employé'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={() => generateAiSuggestions(selectedEmployeeForAI)}
                  disabled={!selectedEmployeeForAI || loadingAiSuggestions}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {loadingAiSuggestions ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" />Générer des suggestions</>
                  )}
                </Button>
              </div>
              
              {/* AI Suggestions list */}
              {aiSuggestions.length > 0 && (
                <div className="space-y-3 mt-4">
                  <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    {aiSuggestions.length} suggestion(s) générée(s)
                  </h4>
                  {aiSuggestions.map((suggestion, idx) => (
                    <Card key={idx} className="bg-white border-purple-200 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1">
                            <h5 className="font-semibold text-slate-900 mb-1">{suggestion.title}</h5>
                            <p className="text-sm text-slate-600 mb-2">{suggestion.description}</p>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="outline" className="text-xs">
                                Poids suggéré: {suggestion.suggested_weight}%
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                {suggestion.timeframe}
                              </Badge>
                              {suggestion.kpis?.slice(0, 2).map((kpi, i) => (
                                <Badge key={i} className="bg-blue-50 text-blue-700 text-xs">
                                  KPI: {kpi.length > 25 ? kpi.substring(0, 25) + '...' : kpi}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => adoptAiSuggestion(suggestion)}
                            className="bg-green-600 hover:bg-green-700 flex-shrink-0"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Adopter
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              {/* Empty state */}
              {!loadingAiSuggestions && aiSuggestions.length === 0 && selectedEmployeeForAI && (
                <p className="text-sm text-purple-600 text-center py-4">
                  Cliquez sur "Générer des suggestions" pour obtenir des propositions d'objectifs personnalisées.
                </p>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* FREE plan upsell */}
        {isFree && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Suggestions IA d'objectifs</p>
                  <p className="text-sm text-amber-700">Disponible avec le plan PRO</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate('/billing/subscription')}>
                Découvrir PRO
              </Button>
            </CardContent>
          </Card>
        )}
        
      </div>
      
      {/* Create dialog */}
      <CreateObjectiveDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={handleCreateObjective}
        departments={departments}
        employees={employees}
        campaigns={campaigns}
        userRole={user?.role}
        isPro={isPro}
      />
      
      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={() => setShowDeleteDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-600">
              Êtes-vous sûr de vouloir supprimer l'objectif <strong>"{objectiveToDelete?.title}"</strong> ?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Cette action est irréversible.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Suppression...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" />Supprimer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View/Edit dialog */}
      <Dialog open={showViewDialog} onOpenChange={() => setShowViewDialog(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editMode ? (
                <><Edit className="w-5 h-5 text-blue-600" />Modifier l'objectif</>
              ) : (
                <><Eye className="w-5 h-5 text-slate-600" />Détails de l'objectif</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedObjective && (
            <div className="space-y-4 py-4">
              {/* Type badge */}
              <div className="flex items-center gap-2">
                <Badge className={OBJECTIVE_TYPES[selectedObjective.type]?.color || 'bg-slate-100'}>
                  {OBJECTIVE_TYPES[selectedObjective.type]?.label || 'Objectif'}
                </Badge>
                <StatusBadge status={selectedObjective.status} />
              </div>
              
              {/* Title */}
              <div>
                <Label className="text-sm font-medium">Titre</Label>
                {editMode ? (
                  <Input
                    value={editFormData.title}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-1"
                  />
                ) : (
                  <p className="text-slate-900 font-semibold mt-1">{selectedObjective.title}</p>
                )}
              </div>
              
              {/* Description */}
              <div>
                <Label className="text-sm font-medium">Description</Label>
                {editMode ? (
                  <Textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1"
                    rows={3}
                  />
                ) : (
                  <p className="text-slate-600 text-sm mt-1">{selectedObjective.description || 'Aucune description'}</p>
                )}
              </div>
              
              {/* Weight */}
              <div>
                <Label className="text-sm font-medium">Pondération: {editMode ? editFormData.weight : selectedObjective.weight}%</Label>
                {editMode ? (
                  <Slider
                    value={[editFormData.weight]}
                    onValueChange={([v]) => setEditFormData(prev => ({ ...prev, weight: v }))}
                    min={5}
                    max={50}
                    step={5}
                    className="mt-2"
                  />
                ) : (
                  <Progress value={selectedObjective.weight * 2} className="h-2 mt-2" />
                )}
              </div>
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Date de début</Label>
                  <p className="text-sm text-slate-600 mt-1">
                    {selectedObjective.startDate ? new Date(selectedObjective.startDate).toLocaleDateString('fr-FR') : 'Non définie'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Échéance</Label>
                  {editMode ? (
                    <Input
                      type="date"
                      value={editFormData.endDate}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, endDate: e.target.value }))}
                      className="mt-1"
                    />
                  ) : (
                    <p className="text-sm text-slate-600 mt-1">
                      {selectedObjective.endDate ? new Date(selectedObjective.endDate).toLocaleDateString('fr-FR') : 'Non définie'}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Evaluations if any */}
              {(selectedObjective.selfEvaluation || selectedObjective.managerEvaluation) && (
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium mb-2 block">Évaluations</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedObjective.selfEvaluation && (
                      <Card className="bg-blue-50">
                        <CardContent className="p-3">
                          <p className="text-xs text-blue-700 font-medium">Auto-évaluation</p>
                          <p className="text-lg font-bold text-blue-900">{selectedObjective.selfEvaluation.score}%</p>
                        </CardContent>
                      </Card>
                    )}
                    {selectedObjective.managerEvaluation && (
                      <Card className="bg-green-50">
                        <CardContent className="p-3">
                          <p className="text-xs text-green-700 font-medium">Évaluation Manager</p>
                          <p className="text-lg font-bold text-green-900">{selectedObjective.managerEvaluation.score}%</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              )}
              
              {/* Created info */}
              <div className="text-xs text-slate-400 pt-2 border-t">
                Créé par {selectedObjective.createdByName || 'Inconnu'} le{' '}
                {selectedObjective.createdAt ? new Date(selectedObjective.createdAt).toLocaleDateString('fr-FR') : 'N/A'}
              </div>
            </div>
          )}
          
          <DialogFooter>
            {editMode ? (
              <>
                <Button variant="outline" onClick={() => setEditMode(false)}>Annuler</Button>
                <Button onClick={handleSaveEdit} disabled={saving}>
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</> : 'Enregistrer'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowViewDialog(false)}>Fermer</Button>
                {(canEdit || selectedObjective?.createdBy === user?.id) && selectedObjective?.status !== 'completed' && (
                  <Button onClick={() => setEditMode(true)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </DashboardLayout>
  );
}
